import React, { useState, useEffect, useRef, useCallback } from "react";
import { gsap } from "gsap";
import placeholderImage from "../../assets/placeholders/picture.webp";

interface LazyLoadImageProps {
  src: string;
  alt: string;
  className?: string;
}

const LazyLoadImage: React.FC<LazyLoadImageProps> = ({
  src,
  alt,
  className,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const onIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    if (entry.isIntersecting) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(onIntersection, {
      rootMargin: "100px",
      threshold: 0.1,
    });

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [onIntersection]);

  const handleImageLoad = () => {
    setIsLoaded(true);
    if (containerRef.current) {
      gsap.fromTo(
        containerRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.5, ease: "power2.out" }
      );
    }
  };

  return (
    <div
      ref={containerRef}
      style={{ position: "relative", overflow: "hidden" }}
      className={className}
    >
      {/* Placeholder Image */}
      {!isLoaded && (
        <img
          src={placeholderImage}
          alt="Placeholder"
          className={`${className} absolute top-0 left-0 w-full h-full object-cover`}
        />
      )}
      {/* Actual Image */}
      {isVisible && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          onLoad={handleImageLoad}
          style={{ opacity: isLoaded ? 1 : 0 }}
          className={`transition-opacity duration-500 ${className}`}
        />
      )}
    </div>
  );
};

export default LazyLoadImage;
